// Major-style Swiss as a record graph (like HLTV's "Group play"): one column per round, the
// matches grouped by the record the teams had going in (0:0 → 1:0 / 0:1 → …), green arrows to
// the winners' next group and red arrows to the losers' one, qualified (3:0, 3:1, 3:2) and
// eliminated (0:3, 1:3, 2:3) teams in a green / red box after the round they finished in.
// Rounds not paired yet show their record groups empty. Admin: click a match → the match panel,
// drag a logo onto another to swap them (same rules as everywhere: useCardDnd). Public: click →
// the panel match page.
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TeamLogo from '../teams/TeamLogo';
import { useCardDnd } from './MatchCard';
import { cardStatus, scoresOf } from './bracketText';
import './bracket.css';

const key = (w, l) => `${w}:${l}`;

/** Record (wins, losses) of every team before round r; byes count as a win. */
function recordsBefore(matches, r) {
  const rec = {};
  const add = (id, k) => { if (!id) return; rec[id] = rec[id] || { w: 0, l: 0 }; rec[id][k]++; };
  for (const m of matches) {
    if (m.round_number >= r || m.status !== 'completed') continue;
    if (m.walkover) { add(m.team1_id || m.team2_id, 'w'); continue; }
    if (m.winner_id) { add(m.winner_id, 'w'); add(m.winner_id === m.team1_id ? m.team2_id : m.team1_id, 'l'); }
  }
  return rec;
}

/** Compact "logo vs logo" match (admin: select + drag; public: link). */
function Duel({ m, teams, readOnly, selected, onSelect, linkPrefix, dnd, hoverTeam, setHoverTeam }) {
  const { t } = useTranslation();
  const st = cardStatus(m);
  const sc = scoresOf(m);
  const done = m.status === 'completed' && m.winner_id;
  const tm = (id) => (id ? teams[id] || { id, name: `#${id}` } : null);
  const a = tm(m.team1_id), b = tm(m.team2_id);
  const tip = m.provisional
    ? t('bracket.major.provisional', 'Already on this record - the opponents are drawn when round {{n}} ends', { n: m.round_number - 1 })
    : `${a ? a.name : t('bracket.tbd', 'TBD')}${sc ? ` ${sc[0]} : ${sc[1]} ` : ' vs '}${b ? b.name : (m.walkover ? t('bracket.bye', 'BYE') : t('bracket.tbd', 'TBD'))} · BO${m.best_of || 1}`;

  const slot = (n) => {
    const id = m[`team${n}_id`];
    const team = n === 1 ? a : b;
    const lost = done && id && m.winner_id !== id;
    const can = !!(dnd && !m.provisional && dnd.canMove(m, n));
    const k = `${m.id}:${n}`;
    const isSrc = dnd && dnd.dragSlot && dnd.dragSlot.match_id === m.id && dnd.dragSlot.slot === n;
    return (
      <span className={`mj-slot${lost ? ' lose' : ''}${hoverTeam && id === hoverTeam ? ' hl' : ''}${can ? ' movable' : ''}${dnd && dnd.dropKey === k ? ' drop' : ''}${isSrc ? ' src' : ''}`}
        title={team ? team.name : undefined}
        onMouseEnter={() => id && setHoverTeam(id)} onMouseLeave={() => setHoverTeam(null)}
        draggable={can}
        onDragStart={can ? (e) => { e.stopPropagation(); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', k); dnd.setDragSlot({ match_id: m.id, slot: n }); } : undefined}
        onDragEnd={dnd ? () => { dnd.setDragSlot(null); dnd.setDropKey(null); } : undefined}
        onDragOver={can && dnd.dragSlot && !isSrc ? (e) => { e.preventDefault(); if (dnd.dropKey !== k) dnd.setDropKey(k); } : undefined}
        onDragLeave={dnd ? () => dnd.dropKey === k && dnd.setDropKey(null) : undefined}
        onDrop={can && dnd.dragSlot && !isSrc ? (e) => { e.preventDefault(); e.stopPropagation(); const src = dnd.dragSlot; dnd.setDragSlot(null); dnd.setDropKey(null); dnd.onSwap(src, { match_id: m.id, slot: n }); } : undefined}>
        {team ? <TeamLogo team={team} size={28} radius={6} /> : <span className="mj-tbd">{m.walkover ? t('bracket.bye', 'BYE') : t('bracket.tbd', 'TBD')}</span>}
      </span>
    );
  };

  const clickable = !m.provisional && (!readOnly || !!m.match_id);
  return (
    <div className={`mj-duel${m.provisional ? ' prov' : ''}${selected ? ' sel' : ''}${st === 'live' ? ' live' : ''}${st === 'decision' ? ' decision' : ''}${m.walkover ? ' bye' : ''}${clickable ? ' click' : ''}`}
      title={tip}
      onClick={() => {
        if (!clickable) return;
        if (readOnly) { window.location.href = `${linkPrefix}/${m.match_id}`; return; }
        onSelect && onSelect(m);
      }}>
      {slot(1)}
      {/* score once the panel match is live (running score: rounds in BO1, maps in BO3/BO5) or finished */}
      <span className={`mj-vs${sc && st === 'live' ? ' livesc' : ''}`}>{sc && (done || st === 'live') ? `${sc[0]}:${sc[1]}` : 'vs'}</span>
      {m.walkover && !m.team2_id ? <span className="mj-slot"><span className="mj-tbd">{t('bracket.bye', 'BYE')}</span></span> : slot(2)}
      {!m.provisional && !m.walkover && <span className={`mj-bo${Number(m.best_of || 1) === 1 ? ' bo1' : ''}`}>BO{m.best_of || 1}</span>}
      {st === 'live' && <span className="mj-live" aria-label={t('bracket.status.live', 'Live')} />}
    </div>
  );
}

export default function MajorSwissView({ bracket, matches, teams, standings, readOnly = false, selectedId, onSelect, onSwap, linkPrefix = '/match' }) {
  const { t } = useTranslation();
  const [hoverTeam, setHoverTeam] = useState(null);
  const dnd = useCardDnd(readOnly ? null : onSwap, matches);
  const info = bracket.swiss_info || {};
  const W = info.wins || 3, L = info.losses || 3;
  const total = Math.max(info.total_rounds || W + L - 1, matches.reduce((mx, m) => Math.max(mx, m.round_number), 0));

  // Columns: the record groups of each round (empty ones for rounds not paired yet) + the
  // qualified / eliminated teams of the rounds before (the last round's go into the last column).
  const cols = useMemo(() => {
    const out = [];
    // How many teams each record group will hold (winners ceil(n/2) - a bye is a win -, losers floor),
    // followed forward from round 1; teams reaching the wins / losses limit leave.
    const N = (standings || []).length || new Set(matches.flatMap(m => [m.team1_id, m.team2_id]).filter(Boolean)).size;
    const expected = [null, new Map([[key(0, 0), N]])];
    for (let r = 1; r < total; r++) {
      const next = new Map();
      for (const [k, c] of expected[r]) {
        const [w, l] = k.split(':').map(Number);
        if (w + 1 < W) next.set(key(w + 1, l), (next.get(key(w + 1, l)) || 0) + Math.ceil(c / 2));
        if (l + 1 < L) next.set(key(w, l + 1), (next.get(key(w, l + 1)) || 0) + Math.floor(c / 2));
      }
      expected.push(next);
    }
    // Records right now (every finished match): a winner shows in its next group at once
    const now = recordsBefore(matches, Infinity);
    const rank = new Map((standings || []).map(s => [s.team_id, s.rank]));
    for (let r = 1; r <= total; r++) {
      const list = matches.filter(m => m.round_number === r).sort((a, b) => a.position - b.position);
      const rec = recordsBefore(matches, r);
      const groups = new Map();
      for (let w = W - 1; w >= 0; w--) for (let l = 0; l < L; l++) if (w + l === r - 1) groups.set(key(w, l), { w, l, list: [] });
      for (const m of list) {
        const x = rec[m.team1_id || m.team2_id] || { w: 0, l: 0 };
        const k = key(x.w, x.l);
        if (!groups.has(k)) groups.set(k, { w: x.w, l: x.l, list: [] });
        groups.get(k).list.push(m);
      }
      // Round not paired yet (it is drawn when the round before ends): provisional duels - the teams
      // already on that record, in standings order, first slots first, the rest TBD.
      if (!list.length) {
        for (const g of groups.values()) {
          const known = Object.entries(now)
            .filter(([, x]) => x.w === g.w && x.l === g.l)
            .map(([id]) => Number(id))
            .sort((a, b) => (rank.get(a) || 99) - (rank.get(b) || 99));
          const slots = Math.max((expected[r] && expected[r].get(key(g.w, g.l))) || 0, known.length);
          const duels = Math.ceil(slots / 2);
          g.list = Array.from({ length: duels }, (_, i) => ({
            id: `p-${r}-${g.w}-${g.l}-${i}`, provisional: true, round_number: r, status: 'pending',
            team1_id: known[i] || null, team2_id: known[duels + i] || null,
          }));
        }
      }
      out.push({ r, groups: [...groups.values()].sort((a, b) => (b.w - a.w) || (a.l - b.l)), bo: list.find(m => !m.walkover)?.best_of, qual: new Map(), elim: new Map() });
    }
    // finished teams → the column after the round they finished in (capped at the last column)
    for (const s of standings || []) {
      if (!s.qualified && !s.eliminated) continue;
      const finished = s.wins + s.losses; // rounds played = the round it finished in
      const col = out[Math.min(finished, total - 1)]; // index finished = column of round finished + 1
      if (!col) continue;
      const box = s.qualified ? col.qual : col.elim;
      const k = key(s.wins, s.losses);
      if (!box.has(k)) box.set(k, []);
      box.get(k).push(s.team_id);
    }
    return out;
  }, [matches, standings, total, W, L]);

  // Arrows between groups (winners → green, losers → red), measured after layout.
  const wrapRef = useRef(null);
  const nodes = useRef(new Map());
  const reg = (id) => (el) => { if (el) nodes.current.set(id, el); else nodes.current.delete(id); };
  const [paths, setPaths] = useState([]);
  const [svgSize, setSvgSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;
    const draw = () => {
      const base = wrap.getBoundingClientRect();
      const zoom = base.width / (wrap.offsetWidth || base.width) || 1; // page zoom (index.css) scales client rects
      const box = (id) => { const el = nodes.current.get(id); if (!el) return null; const b = el.getBoundingClientRect();
        return { l: (b.left - base.left) / zoom + wrap.scrollLeft, r: (b.right - base.left) / zoom + wrap.scrollLeft, y: (b.top + b.height / 2 - base.top) / zoom }; };
      const curve = (a, b) => { const mx = (a.r + b.l) / 2; return `M${a.r},${a.y} C${mx},${a.y} ${mx},${b.y} ${b.l},${b.y}`; };
      const ps = [];
      cols.forEach((c, i) => {
        if (i === cols.length - 1) return;
        c.groups.forEach(g => {
          const from = box(`g-${c.r}-${key(g.w, g.l)}`);
          if (!from) return;
          const nextCol = cols[i + 1];
          const win = g.w + 1 >= W ? box(`q-${nextCol.r}`) : box(`g-${nextCol.r}-${key(g.w + 1, g.l)}`);
          const loss = g.l + 1 >= L ? box(`e-${nextCol.r}`) : box(`g-${nextCol.r}-${key(g.w, g.l + 1)}`);
          const empty = g.list.every(m => m.provisional); // not paired yet
          if (win) ps.push({ d: curve(from, win), cls: `win${empty ? ' dim' : ''}` });
          if (loss) ps.push({ d: curve(from, loss), cls: `loss${empty ? ' dim' : ''}` });
        });
      });
      setPaths(ps);
      setSvgSize({ w: wrap.scrollWidth, h: wrap.scrollHeight });
    };
    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [cols, W, L]);

  const logos = (ids) => ids.map(id => {
    const tm = teams[id] || { id, name: `#${id}` };
    return <span key={id} className={`mj-res-t${hoverTeam === id ? ' hl' : ''}`} title={tm.name} onMouseEnter={() => setHoverTeam(id)} onMouseLeave={() => setHoverTeam(null)}><TeamLogo team={tm} size={30} radius={6} /></span>;
  });
  const resultBox = (map, kind, c) => {
    if (!map.size) return <div className="mj-res-slot" ref={reg(`${kind}-${c.r}`)} />; // arrow target before anyone finished here
    const recs = [...map.keys()].sort((a, b) => (kind === 'q' ? Number(a.split(':')[1]) - Number(b.split(':')[1]) : Number(a.split(':')[0]) - Number(b.split(':')[0])));
    return (
      <div className={`mj-res ${kind === 'q' ? 'q' : 'e'}`} ref={reg(`${kind}-${c.r}`)}
        title={kind === 'q' ? t('bracket.qualified', 'Qualified') : t('bracket.eliminated', 'Eliminated')}>
        {recs.map(k => (
          <div key={k} className="mj-res-col">
            <div className="mj-res-rec">{k}</div>
            {logos(map.get(k))}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="mj-wrap" ref={wrapRef}>
      <svg className="mj-lines" aria-hidden="true" width={svgSize.w} height={svgSize.h}>
        {paths.map((p, i) => <path key={i} d={p.d} className={p.cls} />)}
      </svg>
      {cols.map(c => (
        <div key={c.r} className="mj-col">
          <div className="mj-head">{t('bracket.round.n', 'Round {{n}}', { n: c.r })}{c.bo ? <span> · BO{c.bo}</span> : null}</div>
          <div className="mj-top">{c.r > 1 && resultBox(c.qual, 'q', c)}</div>
          <div className="mj-mid">
            {c.groups.map(g => (
              <div key={key(g.w, g.l)} className={`mj-group${g.list.every(m => m.provisional) ? ' empty' : ''}`} ref={reg(`g-${c.r}-${key(g.w, g.l)}`)}>
                <div className="mj-rec">{key(g.w, g.l)}</div>
                {g.list.length ? g.list.map(m => (
                  <Duel key={m.id} m={m} teams={teams} readOnly={readOnly} selected={selectedId === m.id} onSelect={onSelect}
                    linkPrefix={linkPrefix} dnd={dnd} hoverTeam={hoverTeam} setHoverTeam={setHoverTeam} />
                )) : <div className="mj-wait">{t('bracket.waitingPairing', 'Waiting for pairing')}</div>}
              </div>
            ))}
          </div>
          <div className="mj-bot">{c.r > 1 && resultBox(c.elim, 'e', c)}</div>
        </div>
      ))}
    </div>
  );
}
